import * as z from 'zod';
import {organizationRefSchema} from '../organizations';
import {readImageSchema, updateImageSchema} from "../images";
import {readJobSchema} from "../jobs";

// Nested schemas

export const businessProfileSchema = z.object({
  industry: z.string().nullable(),
  businessModel: z.string().nullable(),
  targetCustomer: z.string().nullable(),
  geography: z.array(z.string()).nullable(),
  languages: z.array(z.string()).nullable(),
});
export type BusinessProfile = z.infer<typeof businessProfileSchema>;

export const valueAndMessagingSchema = z.object({
  brandMessaging: z.string().nullable(),
  keyBenefits: z.string().nullable(),
  painPointsAddressed: z.string().nullable(),
  differentiators: z.string().nullable(),
  vision: z.string().nullable(),
  toneOfVoices: z.array(z.string()).nullable(),
  keywords: z.array(z.string()).nullable(),
  whatToAvoid: z.string().nullable(),
  negativeKeywords: z.array(z.string()).nullable(),
});
export type ValueAndMessaging = z.infer<typeof valueAndMessagingSchema>;

export const visualIdentitySchema = z.object({
  primaryColor: z.string().nullable(),
  secondaryColors: z.array(z.string()).nullable(),
  typography: z.record(z.string(), z.string().nullable()).nullable(),
  imageryStyle: z.array(z.string()).nullable(),
  whatToAvoid: z.string().nullable(),
});
export type VisualIdentity = z.infer<typeof visualIdentitySchema>;

export const complianceSchema = z.object({
  legalLink: z.string().nullable(),
  industryCompliance: z.string().nullable(),
  contentRestriction: z.string().nullable(),
});
export type Compliance = z.infer<typeof complianceSchema>;

export const qualityControlSchema = z.object({
  legalCompliance: z.number().nullable(),
  brandConsistency: z.number().nullable(),
  messagingAlignment: z.number().nullable(),
});
export type QualityControl = z.infer<typeof qualityControlSchema>;

export const personalizationSchema = z.object({
  depth: z.array(z.string()).nullable(),
  useLogo: z.boolean().nullable(),
});
export type Personalization = z.infer<typeof personalizationSchema>;

// Raw SVG markup captured from the brand's site during extraction:
// `nav` is the navbar logo, `logos` any other logo marks found on the page.
export const logoSvgsSchema = z.object({
  nav: z.string().nullable(),
  logos: z.array(z.string()).nullable(),
});
export type LogoSvgs = z.infer<typeof logoSvgsSchema>;

// 1. Reference schema with label field (for dropdowns, selects, links)
export const readBrandRefSchema = z.object({
  id: z.uuid(),
});
export type ReadBrandRef = z.infer<typeof readBrandRefSchema>;

// 2. Minimal reference schema (for foreign keys)
export const brandRefSchema = z.object({
  id: z.uuid(),
});
export type BrandRef = z.infer<typeof brandRefSchema>;

// 3. Read schema - all properties nullable (API response)
export const readBrandSchema = z.object({
  id: z.uuid().nullable(),
  name: z.string(),
  logo: z.object(readImageSchema).nullable(),
  organization: z.object({
    id: z.uuid().nullable(),
    name: z.string().nullable(),
  }).nullable(),
  version: z.number().nullable(),
  businessProfile: businessProfileSchema.nullable(),
  valueAndMessaging: valueAndMessagingSchema.nullable(),
  visualIdentity: visualIdentitySchema.nullable(),
  compliance: complianceSchema.nullable(),
  qualityControl: qualityControlSchema.nullable(),
  personalization: personalizationSchema.nullable(),
  // Extraction artifacts
  sourceUrl: z.string().nullable(),
  // Site screenshot taken during extraction (storage key or resolved URL).
  screenshotKey: z.string().nullable(),
  // Markdown documents produced by the extractor agent.
  designMd: z.string().nullable(),
  voiceMd: z.string().nullable(),
  tasteMd: z.string().nullable(),
  // Where the taste document came from (e.g. 'AGENT'); backend-managed
  // values, so this stays a plain string.
  tasteSource: z.string().nullable(),
  logoSvgs: logoSvgsSchema.nullable(),
  extractionJob: z.lazy(() => readJobSchema).nullable(),
  createdAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime().nullable(),
});
export type ReadBrand = z.infer<typeof readBrandSchema>;

// 4. Create schema - based on DTO serialization groups
export const createBrandSchema = z.object({
  name: z.string(),
  logo: updateImageSchema.nullable() ,
  organization: z.lazy(() => organizationRefSchema),
  businessProfile: businessProfileSchema,
  valueAndMessaging: valueAndMessagingSchema,
  visualIdentity: visualIdentitySchema,
  compliance: complianceSchema,
  qualityControl: qualityControlSchema,
  personalization: personalizationSchema,
});
export type CreateBrand = z.infer<typeof createBrandSchema>;

// The top-level sections of a brand version. Each brand form owns some of
// them: its save carries its own sections from the form, and every other one
// as the version the form was loaded from has it.
export const brandSectionKeys = [
  'name',
  'logo',
  'businessProfile',
  'valueAndMessaging',
  'visualIdentity',
  'compliance',
  'qualityControl',
  'personalization',
] as const;
export type BrandSection = (typeof brandSectionKeys)[number];

// POST body: a brand version is immutable, so every save creates a new one,
// whole, and says which version it was based on. The server refuses it (409,
// error code 5601) when a brand exists and `baseVersion` is not its latest
// version; null means "no brand yet".
export const createBrandRequestSchema = createBrandSchema.extend({
  baseVersion: z.number().nullable(),
});
export type CreateBrandRequest = z.infer<typeof createBrandRequestSchema>;

// Legacy export for backward compatibility
export const brandSchema = readBrandSchema;
export type Brand = ReadBrand;
